import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Compass, Presentation, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { SmartLink } from "@/components/Header";
import { PageMeta } from "@/components/site/blocks";
import { BuddyDoodle } from "@/components/site/HeroDemo";
import { liveProducts, productPath } from "@/data/products";
import { LOGIN_URL, REQUEST_DEMO_URL } from "@/data/site";

interface Option {
  key: string;
  icon?: LucideIcon;
  /** A product's mark, used instead of an icon. */
  logo?: string;
  tint: string;
  title: string;
  body: string;
  href: string;
  cta: string;
}

// "Get started" is product-aware: every live product is a choice, followed by the two
// paths for people who are not ready to sign up. New products appear here on their own.
const options: Option[] = [
  ...liveProducts.map((p) => ({
    key: p.slug,
    logo: p.logo,
    tint: "bg-[hsl(216_90%_96%)]",
    title: `Use ${p.name}`,
    body: p.tagline,
    href: p.startUrl,
    cta: p.download ? "Download free" : "Create free workspace",
  })),
  {
    key: "demo",
    icon: Presentation,
    tint: "bg-[hsl(40_90%_92%)] text-[hsl(32_90%_36%)]",
    title: "See it with a guide",
    body: "A short walkthrough with our team, shaped around how your business talks to customers.",
    href: REQUEST_DEMO_URL,
    cta: "Request a demo",
  },
  {
    key: "explore",
    icon: Compass,
    tint: "bg-[hsl(140_45%_92%)] text-[hsl(150_60%_28%)]",
    title: "Look around first",
    body: "Read what the product does, which tools it connects to, and what it costs.",
    href: liveProducts[0] ? productPath(liveProducts[0]) : "/products",
    cta: "Explore the product",
  },
];

const GetStarted = () => {
  const [picked, setPicked] = useState(options[0].key);
  const current = options.find((o) => o.key === picked)!;

  return (
    <>
      <PageMeta
        title="Get Started Free with ClearMakes"
        description="Pick a ClearMakes product to start with: Inbox for email and chat workflows, Agents for answers from your documents, or ClearMeet for your meetings."
        path="/get-started"
      />
      <main className="flex min-h-[100dvh] flex-col bg-background text-foreground">
        <div className="cm-container flex items-center justify-between py-5">
          <Link to="/" aria-label="ClearMakes home">
            <Logo />
          </Link>
          <SmartLink href={LOGIN_URL} className="text-[14px] text-muted-foreground transition-colors hover:text-foreground">
            Already have an account? <span className="font-medium text-foreground">Log in</span>
          </SmartLink>
        </div>

        <section className="cm-container flex flex-1 flex-col items-center justify-center py-10 text-center sm:py-16">
          <BuddyDoodle className="cm-float h-16 w-16 text-foreground" />
          <h1 className="cm-h2 mt-5">How do you want to start?</h1>
          <p className="cm-lead mt-3">Pick one. You can always do the others later.</p>

          <div role="radiogroup" aria-label="How do you want to start?" className="mt-10 grid w-full max-w-4xl gap-4 text-left sm:grid-cols-3">
            {options.map((o, i) => {
              const on = o.key === picked;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setPicked(o.key)}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.985 }}
                  className={`relative flex h-full flex-col items-start rounded-2xl border bg-background p-6 text-left transition-[border-color,box-shadow] duration-200 ${
                    on ? "border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]" : "border-border hover:border-[hsl(0_0%_70%)]"
                  }`}
                >
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${o.tint}`}>
                    {o.logo ? (
                      <img src={o.logo} alt="" width={28} height={28} className="h-7 w-7" />
                    ) : (
                      o.icon && <o.icon className="h-5 w-5" strokeWidth={1.9} />
                    )}
                  </span>
                  <span className="mt-8 text-[18px] font-semibold tracking-[-0.015em] text-foreground">{o.title}</span>
                  <span className="mt-1.5 text-[14px] leading-relaxed text-muted-foreground">{o.body}</span>
                  <span
                    className={`absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
                      on ? "border-foreground bg-foreground text-background" : "border-border text-transparent"
                    }`}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                </motion.button>
              );
            })}
          </div>

          <SmartLink href={current.href} className="cm-btn-primary cm-btn-lg mt-9 w-full max-w-xs">
            {current.cta}
          </SmartLink>
        </section>
      </main>
    </>
  );
};

export default GetStarted;
